import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext'
import type { ContentAST, ListItem } from '@shared/ast/astTypes'
import { type ProposalWordBlock, proposalWordChanges } from '@shared/proposalWordChanges'
import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { InlineProposal, type InlineProposalReview } from '../InlineProposal'
import { planInlineProposal } from '../inlineProposalPlan'
import { WordProposal } from '../WordProposal'
import './inlineProposals.css'

/** Review layers are siblings of contenteditable, never nodes in the saved/undo document. */
export function InlineProposalsPlugin({ ast, review }: { ast: ContentAST; review: InlineProposalReview }) {
  const [editor] = useLexicalComposerContext()
  const [root, setRoot] = useState<HTMLElement | null>(null)
  const slots = useRef(new Map<string, HTMLDivElement>())
  const plans = useMemo(() => {
    const ranges: Array<{ start: number; end: number }> = []
    return review.proposals.map((item) => {
      const plan = planInlineProposal(ast, item)
      const blocked = !plan.stale && ranges.some((r) => plan.start < r.end && plan.end > r.start)
      if (!plan.stale && !blocked) {
        ranges.push(plan)
      }
      const words = !plan.stale && !blocked ? proposalWordChanges(item.currentText, item.markdown) : null
      return { item, plan, blocked, words }
    })
  }, [ast, review.proposals])
  const entries = useMemo(
    () =>
      plans.flatMap<(typeof plans)[number] & { block: ProposalWordBlock | null; key: string; anchorId: string | null }>(
        (entry) =>
          entry.words
            ? entry.words.blocks.map((block) => ({
                ...entry,
                block,
                key: `${entry.item.id}:${block.blockIndex}`,
                anchorId: ast.blocks[entry.plan.start + block.blockIndex].id,
              }))
            : [{ ...entry, block: null, key: entry.item.id, anchorId: entry.plan.afterBlockId }],
      ),
    [plans, ast],
  )
  useEffect(() => editor.registerRootListener(setRoot), [editor])
  const host = root?.parentElement

  useLayoutEffect(() => {
    if (!root || !host || entries.length === 0) {
      return
    }
    const reserved = new Map<
      HTMLElement,
      { property: 'paddingTop' | 'paddingBottom'; original: string; base: number; applied: string; visibility: string }
    >()
    const marked = new Set<HTMLElement>()
    let frame: number | null = null
    const findBlock = (id: string) => root.querySelector<HTMLElement>(`[data-block-id="${CSS.escape(id)}"]`)
    const sourceBlocks = new Map(ast.blocks.map((block) => [block.id, block]))
    const findItems = (items: ListItem[]): HTMLElement[] =>
      items.flatMap((item) => {
        const element = findBlock(item.id)
        return [...(element ? [element] : []), ...findItems(item.children ?? [])]
      })
    const findBlockElements = (id: string): HTMLElement[] => {
      const block = sourceBlocks.get(id)
      // Lexical merges adjacent lists and drops one wrapper ID. Item IDs survive;
      // use them for both placement and highlighting, without marking the other list.
      if (block?.type === 'list') {
        const items = findItems(block.items)
        if (items.length) {
          return items
        }
      }
      const element = findBlock(id)
      return element ? [element] : []
    }
    const layout = () => {
      frame = null
      const groups = new Map<HTMLElement, typeof entries>()
      const leftEdges = new Map<string, number>()
      for (const entry of entries) {
        const anchors = entry.anchorId ? findBlockElements(entry.anchorId) : [root]
        const anchor = anchors[anchors.length - 1]
        const slot = slots.current.get(entry.key)
        if (!anchor || !slot) {
          if (slot) {
            slot.style.visibility = 'hidden'
          }
          continue
        }
        groups.set(anchor, [...(groups.get(anchor) ?? []), entry])
        const deleted =
          !entry.block && !entry.plan.stale && !entry.blocked
            ? entry.plan.deletedBlockIds.flatMap(findBlockElements)
            : []
        for (const element of deleted) {
          element.classList.add('inline-proposal-deletion')
          marked.add(element)
        }
        // Match the replaced content's width, including list indentation. The
        // final anchor can be more deeply nested than the rest of the replacement.
        const rects = (entry.block ? [anchor] : deleted.length ? deleted : [root]).map((element) =>
          element.getBoundingClientRect(),
        )
        const left = Math.min(...rects.map((rect) => rect.left))
        const width = Math.max(...rects.map((rect) => rect.right)) - left
        leftEdges.set(entry.key, left)
        const style = getComputedStyle(anchor)
        slot.style.width = `${width}px`
        slot.style.fontFamily = style.fontFamily
        slot.style.fontSize = style.fontSize
        slot.style.lineHeight = style.lineHeight
        slot.style.textAlign = style.textAlign
        slot.style.direction = style.direction
        slot.style.fontWeight = style.fontWeight
      }
      for (const [anchor, group] of groups) {
        const property = anchor === root ? 'paddingTop' : 'paddingBottom'
        let saved = reserved.get(anchor)
        if (!saved) {
          saved = {
            property,
            original: anchor.style[property],
            base: parseFloat(getComputedStyle(anchor)[property]) || 0,
            applied: '',
            visibility: anchor.style.visibility,
          }
          reserved.set(anchor, saved)
        }
        let gap: number
        if (group[0].block) {
          const naturalHeight =
            anchor.getBoundingClientRect().height - ((parseFloat(saved.applied) || saved.base) - saved.base)
          gap = Math.max(0, (slots.current.get(group[0].key)?.getBoundingClientRect().height ?? 0) - naturalHeight)
          anchor.style.visibility = 'hidden'
        } else {
          gap = group.reduce(
            (sum, entry) => sum + (slots.current.get(entry.key)?.getBoundingClientRect().height ?? 0) + 12,
            4,
          )
        }
        saved.applied = `${saved.base + gap}px`
        if (anchor.style[property] !== saved.applied) {
          anchor.style[property] = saved.applied
        }
        // CSSOM rounds fractional pixels; retain its value for exact cleanup comparisons.
        saved.applied = anchor.style[property]
      }
      const hostRect = host.getBoundingClientRect()
      for (const [anchor, group] of groups) {
        const saved = reserved.get(anchor)!
        const rect = anchor.getBoundingClientRect()
        const gap = parseFloat(saved.applied) - saved.base
        let top =
          (group[0].block ? rect.top : anchor === root ? rect.top + saved.base : rect.bottom - gap) -
          hostRect.top +
          host.scrollTop -
          host.clientTop +
          (group[0].block ? 0 : 4)
        for (const entry of group) {
          const slot = slots.current.get(entry.key)!
          slot.style.top = `${top}px`
          slot.style.left = `${leftEdges.get(entry.key)! - hostRect.left + host.scrollLeft - host.clientLeft}px`
          slot.style.visibility = 'visible'
          top += slot.getBoundingClientRect().height + 12
        }
      }
    }
    const schedule = () => {
      if (frame === null) {
        frame = requestAnimationFrame(layout)
      }
    }
    const observer = new ResizeObserver(schedule)
    observer.observe(root)
    for (const slot of slots.current.values()) {
      observer.observe(slot)
    }
    const mutations = new MutationObserver(schedule)
    mutations.observe(root, { childList: true, subtree: true })
    const unregister = editor.registerUpdateListener(schedule)
    window.addEventListener('resize', schedule)
    layout()
    return () => {
      if (frame !== null) {
        cancelAnimationFrame(frame)
      }
      observer.disconnect()
      mutations.disconnect()
      unregister()
      window.removeEventListener('resize', schedule)
      for (const [element, saved] of reserved) {
        if (element.style[saved.property] === saved.applied) {
          element.style[saved.property] = saved.original
        }
        element.style.visibility = saved.visibility
      }
      for (const element of marked) {
        element.classList.remove('inline-proposal-deletion')
      }
    }
  }, [editor, host, root, entries, ast])

  if (!host) {
    return null
  }
  return createPortal(
    <>
      {entries.map(({ item, plan, blocked, block, key }) => (
        <div
          key={key}
          ref={(element) => {
            if (element) {
              slots.current.set(key, element)
            } else {
              slots.current.delete(key)
            }
          }}
          dir={ast.meta.dir}
          data-inline-proposal-slot={item.id}
          style={{
            position: 'absolute',
            visibility: 'hidden',
            zIndex: 2,
            fontFamily: 'inherit',
            fontSize: 16,
            lineHeight: 1.5,
          }}
        >
          {block ? (
            <WordProposal item={item} block={block} onAction={review.onAction} />
          ) : (
            <InlineProposal item={item} plan={plan} blocked={blocked} onAction={review.onAction} />
          )}
        </div>
      ))}
    </>,
    host,
  )
}
